import { useMemo } from 'react';

import useOrg from '../useOrg';
import useSharedQueryPoller from '../useSharedQueryPoller';
import {
  MonetaryCreditsDocument,
  useMonetaryCreditsQuery,
  useOrgCreditBalanceQuery,
  useOrgCreditBalancesQuery,
} from './__generated__/queries.generated';
import type { CreditBalance, MonetaryCreditInfo } from './types';

// Once every 5 minutes
const POLLING_PERIOD = 5 * 60 * 1000;

// TODO(Tyron): Remove this once we migrate fully to Orb and rename this file.
export default function useMonetaryCreditInfo(): {
  monetaryCreditInfo: MonetaryCreditInfo | undefined;
  loading: boolean;
} {
  const { data, loading } = useMonetaryCreditsQuery({
    fetchPolicy: 'cache-first',
  });

  useSharedQueryPoller(MonetaryCreditsDocument, {}, POLLING_PERIOD);

  const monetaryCreditInfoRaw = data?.organization;

  const monetaryCreditInfo = useMemo(
    (): MonetaryCreditInfo =>
      monetaryCreditInfoRaw && {
        ...monetaryCreditInfoRaw,
        // Ensure that the credit balance is never negative
        creditBalance: Math.max(0, monetaryCreditInfoRaw.creditBalance),
      },
    [monetaryCreditInfoRaw],
  );

  return {
    monetaryCreditInfo,
    loading,
  };
}

export function useOrgSharedEndpointsCreditBalance(): {
  credits?: {
    remaining: number;
    issued: number;
  };
  loading: boolean;
} {
  const org = useOrg();
  const isOrbBillingEnabled = org.orbMigrationCompletedAt;
  const { data, loading } = useOrgCreditBalanceQuery({
    fetchPolicy: 'cache-first',
    skip: isOrbBillingEnabled,
  });

  const sharedEndpointsCreditBalance = data?.organization.sharedEndpointsCreditBalance;

  return {
    credits: sharedEndpointsCreditBalance && {
      remaining: parseFloat(sharedEndpointsCreditBalance.remaining),
      issued: parseFloat(sharedEndpointsCreditBalance.issued),
    },
    loading,
  };
}

export function useOrgCreditBalances(): {
  creditBalances?: CreditBalance[];
  loading: boolean;
} {
  const org = useOrg();
  const isOrbBillingEnabled = org.orbMigrationCompletedAt;
  const { data, loading } = useOrgCreditBalancesQuery({
    fetchPolicy: 'cache-first',
    skip: !isOrbBillingEnabled,
  });

  const creditBalances = data?.organization?.orgCreditBalances;

  return {
    creditBalances: creditBalances?.map((balance) => ({
      category: balance.category,
      remaining: parseFloat(balance.remaining),
      issued: parseFloat(balance.issued),
    })),
    loading,
  };
}
